/**
 * Loyalty Rewards Agent
 * Tracks customer loyalty and generates reward recommendations
 */

interface LoyaltyMember {
  id: string;
  name: string;
  email: string;
  joinDate: Date;
  totalSpent: number;
  visitCount: number;
  favoriteProducts: string[];
  favoriteFarms: string[];
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  points: number;
}

interface RewardOption {
  id: string;
  name: string;
  description: string;
  pointsCost: number;
  category: 'discount' | 'freeItem' | 'experience' | 'exclusive';
  validFor: number; // days
  farmId?: string;
}

interface EarnedReward {
  reward: RewardOption;
  earnedDate: Date;
  expiryDate: Date;
  redeemed: boolean;
  redeemedDate?: Date;
}

class LoyaltyRewardsAgent {
  private tierThresholds = {
    bronze: 0,
    silver: 500,
    gold: 2000,
    platinum: 5000,
  };

  private pointsPerDollar = 10;

  /**
   * Calculate loyalty tier based on total spent
   */
  calculateTier(totalSpent: number): LoyaltyMember['tier'] {
    if (totalSpent >= this.tierThresholds.platinum) return 'platinum';
    if (totalSpent >= this.tierThresholds.gold) return 'gold';
    if (totalSpent >= this.tierThresholds.silver) return 'silver';
    return 'bronze';
  }

  /**
   * Calculate points from purchase
   */
  calculatePoints(amount: number, tier: LoyaltyMember['tier']): number {
    const multipliers = {
      bronze: 1,
      silver: 1.25,
      gold: 1.5,
      platinum: 2,
    };
    return Math.floor(amount * this.pointsPerDollar * multipliers[tier]);
  }

  /**
   * Get available rewards for member
   */
  getAvailableRewards(member: LoyaltyMember): RewardOption[] {
    const allRewards = this.getAllRewards();
    return allRewards.filter(reward => reward.pointsCost <= member.points);
  }

  /**
   * Get all possible rewards
   */
  getAllRewards(): RewardOption[] {
    return [
      {
        id: 'discount-5',
        name: '5% Off Next Purchase',
        description: 'Get 5% off your next purchase at any participating farm',
        pointsCost: 100,
        category: 'discount',
        validFor: 30,
      },
      {
        id: 'discount-10',
        name: '10% Off Next Purchase',
        description: 'Get 10% off your next purchase at any participating farm',
        pointsCost: 200,
        category: 'discount',
        validFor: 30,
      },
      {
        id: 'discount-15',
        name: '15% Off Next Purchase',
        description: 'Get 15% off your next purchase at any participating farm',
        pointsCost: 350,
        category: 'discount',
        validFor: 30,
      },
      {
        id: 'free-herbs',
        name: 'Free Herb Bundle',
        description: 'Redeem for a free bundle of fresh herbs',
        pointsCost: 150,
        category: 'freeItem',
        validFor: 14,
      },
      {
        id: 'free-eggs',
        name: 'Free Dozen Eggs',
        description: 'Redeem for a free dozen farm-fresh eggs',
        pointsCost: 250,
        category: 'freeItem',
        validFor: 14,
      },
      {
        id: 'free-jam',
        name: 'Free Artisan Jam',
        description: 'Redeem for a jar of homemade artisan jam',
        pointsCost: 300,
        category: 'freeItem',
        validFor: 30,
      },
      {
        id: 'farm-tour',
        name: 'Private Farm Tour',
        description: 'Enjoy a private behind-the-scenes farm tour for two',
        pointsCost: 750,
        category: 'experience',
        validFor: 90,
      },
      {
        id: 'cooking-class',
        name: 'Farm-to-Table Cooking Class',
        description: 'Join an exclusive cooking class featuring local ingredients',
        pointsCost: 1000,
        category: 'experience',
        validFor: 90,
      },
      {
        id: 'early-access',
        name: 'Early Season Access',
        description: 'Get early access to seasonal produce before public release',
        pointsCost: 500,
        category: 'exclusive',
        validFor: 60,
      },
      {
        id: 'csa-discount',
        name: 'CSA Subscription Discount',
        description: 'Get $50 off your next CSA subscription',
        pointsCost: 1500,
        category: 'exclusive',
        validFor: 120,
      },
    ];
  }

  /**
   * Get personalized reward recommendations
   */
  getRecommendedRewards(member: LoyaltyMember): RewardOption[] {
    const available = this.getAvailableRewards(member);
    const recommendations: RewardOption[] = [];

    // Recommend based on purchase history
    if (member.visitCount > 10 && member.points >= 750) {
      const tour = available.find(r => r.id === 'farm-tour');
      if (tour) recommendations.push(tour);
    }

    // Recommend based on tier
    if (member.tier === 'gold' || member.tier === 'platinum') {
      const exclusive = available.filter(r => r.category === 'exclusive');
      recommendations.push(...exclusive);
    }

    // Always suggest best value discounts
    const discounts = available.filter(r => r.category === 'discount');
    if (discounts.length > 0) {
      recommendations.push(discounts[discounts.length - 1]); // Highest discount
    }

    return recommendations.slice(0, 4);
  }

  /**
   * Calculate points needed for next tier
   */
  getNextTierProgress(member: LoyaltyMember): {
    currentTier: string;
    nextTier: string | null;
    amountNeeded: number;
    progress: number;
  } {
    const tiers = ['bronze', 'silver', 'gold', 'platinum'] as const;
    const currentIndex = tiers.indexOf(member.tier);

    if (currentIndex === tiers.length - 1) {
      return {
        currentTier: member.tier,
        nextTier: null,
        amountNeeded: 0,
        progress: 100,
      };
    }

    const nextTier = tiers[currentIndex + 1];
    const nextThreshold = this.tierThresholds[nextTier];
    const currentThreshold = this.tierThresholds[member.tier];
    const amountNeeded = nextThreshold - member.totalSpent;
    const progress = ((member.totalSpent - currentThreshold) / (nextThreshold - currentThreshold)) * 100;

    return {
      currentTier: member.tier,
      nextTier,
      amountNeeded: Math.max(0, amountNeeded),
      progress: Math.min(100, Math.max(0, progress)),
    };
  }

  /**
   * Get tier benefits
   */
  getTierBenefits(tier: LoyaltyMember['tier']): string[] {
    const benefits: { [key: string]: string[] } = {
      bronze: [
        'Earn 10 points per dollar spent',
        'Birthday bonus: 100 extra points',
        'Access to member-only promotions',
      ],
      silver: [
        'Earn 12.5 points per dollar spent',
        'Birthday bonus: 200 extra points',
        'Early access to seasonal sales',
        'Free recipe cards with purchase',
      ],
      gold: [
        'Earn 15 points per dollar spent',
        'Birthday bonus: 500 extra points',
        'Priority checkout at markets',
        'Exclusive monthly specials',
        'Free delivery on orders over $50',
      ],
      platinum: [
        'Earn 20 points per dollar spent',
        'Birthday bonus: 1000 extra points',
        'VIP farm tour invitations',
        'Personal shopping assistant',
        'Free delivery on all orders',
        'Annual gift basket',
      ],
    };

    return benefits[tier];
  }

  /**
   * Generate loyalty summary
   */
  generateLoyaltySummary(member: LoyaltyMember): {
    points: number;
    tier: string;
    tierProgress: any;
    availableRewards: number;
    recommendedReward: RewardOption | null;
    benefits: string[];
  } {
    const tierProgress = this.getNextTierProgress(member);
    const availableRewards = this.getAvailableRewards(member);
    const recommended = this.getRecommendedRewards(member);

    return {
      points: member.points,
      tier: member.tier,
      tierProgress,
      availableRewards: availableRewards.length,
      recommendedReward: recommended[0] || null,
      benefits: this.getTierBenefits(member.tier),
    };
  }

  /**
   * Calculate bonus points for special events
   */
  calculateBonusPoints(
    basePoints: number,
    event: 'double_points' | 'seasonal_bonus' | 'referral' | 'review'
  ): number {
    const multipliers = {
      double_points: 2,
      seasonal_bonus: 1.5,
      referral: 500, // Flat bonus
      review: 50, // Flat bonus
    };

    if (event === 'referral' || event === 'review') {
      return multipliers[event];
    }

    return Math.floor(basePoints * multipliers[event]);
  }
}

const loyaltyRewardsAgent = new LoyaltyRewardsAgent();
export default loyaltyRewardsAgent;
